import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import {
    Box,
    TextField,
    Button,
    Grid,
    alpha,
    Typography,
} from "@mui/material";
import Loader from "../components/Loader";
import CaseHistoryItem from "../cards/CaseHistoryItem";
import { setFullScreenEmail } from "../redux/actions/userActions";
import useEmailByCaseActivityId from "../hooks/queries/useEmailByCaseActivityId";
import { makeStyles } from "@mui/styles";
import { searchEmails } from "../services/searchService";
import { setSnackAction } from "../redux/actions/snackActions";

const useStyles = makeStyles((theme) => ({
    container: {
        background: theme.palette.common.white,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        borderRadius: 4,
        padding: theme.spacing(3),
        margin: theme.spacing(3, 0),
        cursor: "pointer",
        '& em': {
            color: 'purple'
        },
        overflow: 'hidden',
        textOverflow: 'ellipsis',       
    },
    textBold: {
        fontSize: 13,
        fontWeight: 600,
        wordBreak: "break-all",
    },
    card: {
        marginBottom: theme.spacing(2),
    },
    perfScroll: {
        maxHeight: "calc(100vh - 505px)",
        marginTop: '10px'
    },
}));

const SearchTool = ({
    caseState,
    setFullScreenEmail,
    internalUsers,
}) => {

    const classes = useStyles();

    const { currentCaseId: caseId, cases } = caseState;

    const c = cases[caseId];

    const [results, setResults] = useState();


    const emails = useMemo(() => {
        return c.events?.filter(e => e.itemType === "Email");
    }, [c.events]);

    const [emailActivity, setEmailActivity] = useState();

    const [isSearching, setIsSearching] = useState(false);
    const [searchText, setSearchText] = useState("");

    useEmailByCaseActivityId({
        caseActivityId: emailActivity?.caseActivityId,
        onSuccess: (email) => {
            setFullScreenEmail(
                {
                    ...email.data,
                    ...emailActivity,
                    content: email.data.content,
                    person: email.data.person,
                }
            );
            setEmailActivity(null);
        },
    });

    const doSearch = async () => {
        setIsSearching(true);

        try {
            let dto = await searchEmails(caseId, searchText);
            setResults(dto);
        }
        finally {
            setIsSearching(false);
        }
    }

    const emailRow = (item, isSemantic) => {
        let e = emails.find(x => x.replyingToEmailId === item.id)

        return (
            <React.Fragment key={item.id}>
                <Grid item xs={8}>
                    {e ? (
                        <CaseHistoryItem
                            caseEvent={e}
                            internalUsers={internalUsers}
                            handlePreviewClick={() => setEmailActivity(e)}
                        />
                    ) : (
                        <div className={classes.container}><h6>Not found</h6></div>
                    )}
                </Grid>
                <Grid item xs={4}>
                    <div className={classes.container} display="flex">
                        <Typography color="primary" variant="body2" className={classes.textBold}>
                            Score: {isSemantic ? `${(item.score * 100).toFixed(1)}%` : item.rerankerScore.toFixed(2)}
                        </Typography>
                        <div dangerouslySetInnerHTML={{ __html: item.highlights || item.text }} />
                    </div>
                </Grid>
            </React.Fragment>
        )
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }} >
            <Box sx={{ display: 'flex' }}>
                <TextField
                    sx={{ flexGrow: 1, mr: 2 }}
                    variant="outlined"
                    size="small"
                    label="Search text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    disabled={isSearching}
                />
                <Button disabled={isSearching || searchText.trim().length === 0} variant="contained" size="small" onClick={doSearch}>Search</Button>
            </Box>
            {emails && <Box>Total {emails.length} emails</Box>}
            {isSearching && <Loader />}
            {!isSearching && !results && <h1>No results</h1> }
            {!isSearching && results &&
                <>
                    {results.semanticResults &&
                        <>
                            <h1>Semantic</h1>
                            <Grid container spacing={3}>
                                {results.semanticResults.map(e => emailRow(e, true))}
                            </Grid>
                        </>
                    }
                    <h1>General</h1>
                    <Grid container spacing={3}>
                        {results.generalResults.map(e => emailRow(e, false))}
                    </Grid>
                </>
            }
        </Box>
    );
};

const mapStateToProps = (state) => ({
    caseState: state.caseReducer,
    internalUsers: state.userReducer.users,
});

const mapDispatchToProps = (dispatch) => {
    return {
        setFullScreenEmail: (email) => dispatch(setFullScreenEmail(email)),
        setSnack: (email) => dispatch(setSnackAction(email)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchTool);
